import React, { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Upload, FileSpreadsheet, ArrowRight, ArrowLeft, Check,
  AlertCircle, Download, CheckCircle2, XCircle, Loader2,
} from "lucide-react";
import { PageHeader, AppButton, BackButton, AppCard, AppCardContent } from "../ui";
import CommonToaster from "../Common/CommonToaster";
import apiClient from "../../utils/apiClient";
import "./ImportStudents.scss";

const STEPS = [
  { label: "Upload File", icon: Upload },
  { label: "Map Columns", icon: FileSpreadsheet },
  { label: "Preview & Import", icon: Check },
];

const ImportStudentsPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Step 1
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Step 2 (from parse response)
  const [headers, setHeaders] = useState([]);
  const [systemFields, setSystemFields] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [suggestedMapping, setSuggestedMapping] = useState({});
  const [mapping, setMapping] = useState({});
  const [previewRows, setPreviewRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);

  // Step 3 (from validate response)
  const [validationResult, setValidationResult] = useState(null);

  // Final result
  const [importResult, setImportResult] = useState(null);

  // ── Step 1: File Upload ──────────────────────────────────────────────────

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;
    const ext = selectedFile.name.split(".").pop().toLowerCase();
    if (!["csv", "xlsx", "xls"].includes(ext)) {
      setToast({ show: true, message: "Only .csv, .xlsx, and .xls files are supported.", type: "error" });
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      setToast({ show: true, message: "File size must be less than 10 MB.", type: "error" });
      return;
    }
    setFile(selectedFile);
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFileSelect(e.dataTransfer.files[0]);
  }, []);

  const handleParseFile = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await apiClient.post("/students/import/parse", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setHeaders(data.headers || []);
      setSystemFields(data.systemFields || []);
      setSuggestedMapping(data.suggestedMapping || {});
      setMapping(data.suggestedMapping || {});
      setPreviewRows(data.previewRows || []);
      setTotalRows(data.totalRows || 0);
      setStep(1);
    } catch (e) {
      setToast({
        show: true,
        message: e.response?.data?.message || "Failed to parse file.",
        type: "error",
      });
    }
    setLoading(false);
  };

  // ── Step 2: Column Mapping ───────────────────────────────────────────────

  const handleMappingChange = (fileCol, sysField) => {
    setMapping((prev) => ({ ...prev, [fileCol]: sysField }));
  };

  const getMappedFieldsCount = () => {
    return Object.values(mapping).filter((v) => v && v !== "_skip").length;
  };

  const getRequiredFieldsMapped = () => {
    const required = systemFields.filter((f) => f.required).map((f) => f.key);
    const mapped = Object.values(mapping);
    return required.every((f) => mapped.includes(f));
  };

  const isFieldAlreadyMapped = (fieldKey, currentCol) => {
    return Object.entries(mapping).some(
      ([col, val]) => val === fieldKey && col !== currentCol
    );
  };

  const handleValidate = async () => {
    if (!getRequiredFieldsMapped()) {
      setToast({
        show: true,
        message: "Please map all required fields (Name, Mobile).",
        type: "error",
      });
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("mapping", JSON.stringify(mapping));
      const { data } = await apiClient.post("/students/import/validate", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setValidationResult(data);
      setStep(2);
    } catch (e) {
      setToast({
        show: true,
        message: e.response?.data?.message || "Validation failed.",
        type: "error",
      });
    }
    setLoading(false);
  };

  // ── Step 3: Import ──────────────────────────────────────────────────────

  const handleImport = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("mapping", JSON.stringify(mapping));
      formData.append("skipErrors", "true");
      const { data } = await apiClient.post("/students/import/execute", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      setToast({
        show: true,
        message: `${data.successCount} students imported successfully!`,
        type: "success",
      });
    } catch (e) {
      setToast({
        show: true,
        message: e.response?.data?.message || "Import failed.",
        type: "error",
      });
    }
    setLoading(false);
  };

  const downloadErrorReport = () => {
    const errors = importResult?.failedRows || validationResult?.errorRows || [];
    if (!errors.length) return;

    const csvRows = [["Row #", "Errors", "Original Data"]];
    errors.forEach((row) => {
      csvRows.push([
        row.rowNumber,
        row.errors.join("; "),
        JSON.stringify(row.data || row.originalData || {}),
      ]);
    });

    const csvContent = csvRows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "import_errors.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  // ── Stepper UI ───────────────────────────────────────────────────────────

  const renderStepper = () => (
    <div className="import-stepper">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const isActive = i === step;
        const isDone = i < step || importResult;
        return (
          <React.Fragment key={i}>
            <div className={`import-stepper__step ${isActive ? "active" : ""} ${isDone ? "done" : ""}`}>
              <div className="import-stepper__icon">
                {isDone ? <CheckCircle2 size={20} /> : <Icon size={20} />}
              </div>
              <span className="import-stepper__label">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`import-stepper__line ${isDone ? "done" : ""}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );

  // ── Step 1: Upload UI ────────────────────────────────────────────────────

  const renderUploadStep = () => (
    <AppCard>
      <AppCardContent>
        <div
          className={`import-dropzone ${dragActive ? "import-dropzone--active" : ""} ${file ? "import-dropzone--has-file" : ""}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => handleFileSelect(e.target.files[0])}
            className="import-dropzone__input"
          />
          {file ? (
            <div className="import-dropzone__file">
              <FileSpreadsheet size={32} className="import-dropzone__file-icon" />
              <div className="import-dropzone__file-info">
                <span className="import-dropzone__file-name">{file.name}</span>
                <span className="import-dropzone__file-size">
                  {(file.size / 1024).toFixed(1)} KB
                </span>
              </div>
              <AppButton
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setFile(null);
                }}
              >
                Remove
              </AppButton>
            </div>
          ) : (
            <div className="import-dropzone__placeholder">
              <Upload size={40} className="import-dropzone__upload-icon" />
              <p className="import-dropzone__text">
                Drag & drop your file here, or <span className="import-dropzone__link">browse</span>
              </p>
              <p className="import-dropzone__hint">Supports .csv, .xlsx, .xls (max 10 MB)</p>
            </div>
          )}
        </div>

        <div className="import-actions">
          <AppButton
            variant="default"
            disabled={!file || loading}
            onClick={handleParseFile}
          >
            {loading ? <Loader2 size={16} className="spin" /> : <ArrowRight size={16} />}
            Continue
          </AppButton>
        </div>
      </AppCardContent>
    </AppCard>
  );

  // ── Step 2: Mapping UI ──────────────────────────────────────────────────

  const renderMappingStep = () => {
    return (
      <AppCard>
        <AppCardContent>
          <div className="import-mapping-info">
            <p>
              <strong>{totalRows}</strong> rows found. Map your file columns to system fields below.
            </p>
            <p className="import-mapping-info__status">
              {getMappedFieldsCount()} of {headers.length} columns mapped
              {!getRequiredFieldsMapped() && (
                <span className="import-mapping-info__warning">
                  <AlertCircle size={14} /> Required fields missing
                </span>
              )}
            </p>
          </div>

          <div className="import-mapping-table-wrap">
            <table className="import-mapping-table">
              <thead>
                <tr>
                  <th>File Column</th>
                  <th>Sample Data</th>
                  <th>Map To</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {headers.map((header) => {
                  const mappedTo = mapping[header] || "";
                  const isMapped = mappedTo && mappedTo !== "_skip";
                  const sampleValues = previewRows
                    .slice(0, 3)
                    .map((r) => r[header])
                    .filter(Boolean)
                    .join(", ");

                  return (
                    <tr key={header} className={isMapped ? "mapped" : ""}>
                      <td className="import-mapping-table__col-name">
                        <FileSpreadsheet size={14} />
                        {header}
                      </td>
                      <td className="import-mapping-table__sample">
                        {sampleValues || <span className="text-muted">—</span>}
                      </td>
                      <td>
                        <select
                          className="import-mapping-table__select"
                          value={mappedTo}
                          onChange={(e) => handleMappingChange(header, e.target.value)}
                        >
                          <option value="">— Skip —</option>
                          {systemFields.map((sf) => (
                            <option
                              key={sf.key}
                              value={sf.key}
                              disabled={isFieldAlreadyMapped(sf.key, header)}
                            >
                              {sf.label} {sf.required ? "*" : ""}
                              {isFieldAlreadyMapped(sf.key, header) ? " (already mapped)" : ""}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {isMapped ? (
                          <CheckCircle2 size={16} className="import-status-icon import-status-icon--success" />
                        ) : (
                          <span className="import-status-icon import-status-icon--skip">Skip</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="import-actions">
            <AppButton variant="outline" onClick={() => setStep(0)}>
              <ArrowLeft size={16} /> Back
            </AppButton>
            <AppButton
              variant="default"
              disabled={!getRequiredFieldsMapped() || loading}
              onClick={handleValidate}
            >
              {loading ? <Loader2 size={16} className="spin" /> : <ArrowRight size={16} />}
              Validate & Preview
            </AppButton>
          </div>
        </AppCardContent>
      </AppCard>
    );
  };

  // ── Step 3: Preview & Import UI ──────────────────────────────────────────

  const renderPreviewStep = () => {
    if (importResult) {
      return (
        <AppCard>
          <AppCardContent>
            <div className="import-result">
              <div className="import-result__icon">
                <CheckCircle2 size={48} />
              </div>
              <h3 className="import-result__title">Import Complete!</h3>
              <div className="import-result__stats">
                <div className="import-result__stat import-result__stat--success">
                  <CheckCircle2 size={20} />
                  <span>{importResult.successCount} Imported</span>
                </div>
                {importResult.failedCount > 0 && (
                  <div className="import-result__stat import-result__stat--error">
                    <XCircle size={20} />
                    <span>{importResult.failedCount} Failed</span>
                  </div>
                )}
              </div>
              {importResult.failedCount > 0 && (
                <AppButton variant="outline" onClick={downloadErrorReport}>
                  <Download size={16} /> Download Error Report
                </AppButton>
              )}
              <div className="import-actions">
                <AppButton variant="default" onClick={() => navigate("/students")}>
                  Go to Students
                </AppButton>
              </div>
            </div>
          </AppCardContent>
        </AppCard>
      );
    }

    if (!validationResult) return null;

    return (
      <AppCard>
        <AppCardContent>
          <div className="import-preview-summary">
            <div className="import-preview-stat import-preview-stat--total">
              <span className="import-preview-stat__value">{validationResult.totalRows}</span>
              <span className="import-preview-stat__label">Total Rows</span>
            </div>
            <div className="import-preview-stat import-preview-stat--valid">
              <CheckCircle2 size={18} />
              <span className="import-preview-stat__value">{validationResult.validCount}</span>
              <span className="import-preview-stat__label">Valid</span>
            </div>
            <div className="import-preview-stat import-preview-stat--error">
              <XCircle size={18} />
              <span className="import-preview-stat__value">{validationResult.errorCount}</span>
              <span className="import-preview-stat__label">Errors</span>
            </div>
          </div>

          {validationResult.errorCount > 0 && (
            <div className="import-errors-section">
              <div className="import-errors-header">
                <h4>
                  <AlertCircle size={16} /> Rows with Errors
                </h4>
                <AppButton variant="ghost" size="sm" onClick={downloadErrorReport}>
                  <Download size={14} /> Download Errors
                </AppButton>
              </div>
              <div className="import-errors-table-wrap">
                <table className="import-errors-table">
                  <thead>
                    <tr>
                      <th>Row #</th>
                      <th>Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validationResult.errorRows.map((row, i) => (
                      <tr key={i}>
                        <td>{row.rowNumber}</td>
                        <td>
                          {row.errors.map((err, j) => (
                            <span key={j} className="import-error-tag">
                              {err}
                            </span>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {validationResult.validCount > 0 && validationResult.validRows?.length > 0 && (
            <div className="import-valid-section">
              <h4><CheckCircle2 size={16} /> Valid Records Preview</h4>
              <div className="import-valid-table-wrap">
                <table className="import-valid-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Mobile</th>
                      <th>Email</th>
                      <th>Course</th>
                      <th>Batch</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validationResult.validRows.slice(0, 20).map((row, i) => (
                      <tr key={i}>
                        <td>{row.rowNumber}</td>
                        <td>{row.data.name}</td>
                        <td>{row.data.mobile}</td>
                        <td>{row.data.email || "—"}</td>
                        <td>{row.data.courseName || "—"}</td>
                        <td>{row.data.batchName || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="import-actions">
            <AppButton variant="outline" onClick={() => setStep(1)}>
              <ArrowLeft size={16} /> Back
            </AppButton>
            {validationResult.validCount > 0 && (
              <AppButton
                variant="default"
                disabled={loading}
                onClick={handleImport}
              >
                {loading ? <Loader2 size={16} className="spin" /> : <Upload size={16} />}
                Import {validationResult.validCount} Students
              </AppButton>
            )}
          </div>
        </AppCardContent>
      </AppCard>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="import-students-page sl-page">
      <PageHeader
        icon={Upload}
        title="Import Students"
        description="Bulk import students from Excel or CSV files"
        actions={<BackButton onClick={() => navigate("/students")} />}
      />

      {renderStepper()}

      <div className="import-students-page__content">
        {step === 0 && renderUploadStep()}
        {step === 1 && renderMappingStep()}
        {step === 2 && renderPreviewStep()}
      </div>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default ImportStudentsPage;
